import type { UseQueryResult } from "@tanstack/react-query";

type PendingQueryState = {
  isPending: true;
  isError: false;
  error: null;
  data: null;
};
type ErrorQueryState = {
  isPending: false;
  isError: true;
  error: Error | null;
  data: null;
};
type SuccessQueryState<T> = {
  isPending: false;
  isError: false;
  error: null;
  data: T;
};

type QueriesState<T extends readonly UseQueryResult[]> =
  | PendingQueryState
  | ErrorQueryState
  | SuccessQueryState<{
      [K in keyof T]: T[K] extends UseQueryResult<infer U> ? U : never;
    }>;

/**
 * Combines multiple React Query results into a single state object.
 *
 * Note that unlike `useQueries`, this hook combines the results instead of
 * returning an array of results. This is useful for components that need to
 * show a loading screen or error screen if any of the queries are loading or
 * have errored.
 *
 * Note also that the results are "all or nothing." If any query is pending,
 * the combined state is pending. If any query has errored, the combined state
 * is errored. Only when all queries are successful is the combined state
 * successful, and only then is the `data` field populated.
 *
 * @example
 * ```tsx
 * const query1 = useQuery(...);
 * const query2 = useQuery(...);
 * const { isPending, isError, error, data } = useCombinedQueries(query1, query2);
 *
 * if (isPending) return <LoadingScreen />;
 * if (isError) return <ErrorScreen error={error} />;
 *
 * // `data` is a tuple matching the input queries
 * const [data1, data2] = data;
 *
 * return <MyComponent data1={data1} data2={data2} />;
 * ```
 */
export function useCombinedQueries<T extends readonly UseQueryResult[]>(
  ...queries: T
): QueriesState<T> {
  const isError = queries.some((q) => q.isError);
  const error = queries.find((q) => q.isError)?.error ?? null;

  if (isError) {
    return { isPending: false, isError: true, error, data: null };
  }

  const isPending = queries.some((q) => q.isPending);

  if (isPending) {
    return { isPending: true, isError: false, error: null, data: null };
  }

  // This will be a tuple matching the input queries
  const data = queries.map((q) => q.data) as {
    [K in keyof T]: T[K] extends UseQueryResult<infer U> ? U : never;
  };

  return {
    isPending: false,
    isError: false,
    error: null,
    data: data,
  };
}
